import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TeamFormation } from '../types';
import { 
  Formation, 
  getFormationsByAgeGroup,
  FORMATION_GUIDANCE 
} from '../data/formations';
import { Button, Label } from '../styles/GlobalStyles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const ModeToggle = styled.div`
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  padding: var(--spacing-md);
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
`;

const ModeButton = styled.button<{ $active: boolean }>`
  padding: var(--spacing-sm) var(--spacing-md);
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
  }
`;

const FormationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  background: var(--card-background);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`;

const InfoButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.875rem;

  &:hover {
    opacity: 0.8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  background: white;
  cursor: pointer;

  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const CustomFormationInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const Input = styled.input`
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 1rem;

  &:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;

const FormationPreview = styled.div`
  padding: var(--spacing-md);
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const InfoModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
`;

const InfoModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const InfoModalTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 var(--spacing-md) 0;
`;

const InfoModalDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
`;

const InfoModalTips = styled.ul`
  list-style: disc;
  padding-left: var(--spacing-lg);
  color: var(--text-secondary);
  line-height: 1.6;
`;

const WarningMessage = styled.div`
  padding: var(--spacing-md);
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: var(--border-radius-md);
  color: #92400e;
  font-size: 0.875rem;
`;

const RecommendationBadge = styled.span`
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--primary-color);
  color: white;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: var(--spacing-sm);
`;

type FormationType = 'primaryInPossession' | 'primaryOutOfPossession' | 'secondaryInPossession' | 'secondaryOutOfPossession';

interface FormationSelectorProps {
  ageGroup: string;
  formations: {
    primaryInPossession?: TeamFormation;
    primaryOutOfPossession?: TeamFormation;
    secondaryInPossession?: TeamFormation;
    secondaryOutOfPossession?: TeamFormation;
  };
  onChange: (formations: {
    primaryInPossession?: TeamFormation;
    primaryOutOfPossession?: TeamFormation;
    secondaryInPossession?: TeamFormation;
    secondaryOutOfPossession?: TeamFormation;
  }) => void;
}

export const FormationSelector: React.FC<FormationSelectorProps> = ({
  ageGroup,
  formations,
  onChange
}) => {
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [availableFormations, setAvailableFormations] = useState<Formation[]>([]);
  const [showInfo, setShowInfo] = useState<FormationType | null>(null);
  const [customFormation, setCustomFormation] = useState<{ [key in FormationType]?: { notation: string; name: string } }>({});
  const prevAgeGroupRef = useRef<string>(ageGroup);
  const formationsRef = useRef(formations);
  
  // Keep formations ref up to date
  useEffect(() => {
    formationsRef.current = formations;
  }, [formations]);

  useEffect(() => {
    if (ageGroup) {
      const formationsForAge = getFormationsByAgeGroup(ageGroup);
      setAvailableFormations(formationsForAge);
      
      // If age group changed (not initial render), check if current formations are still valid
      if (prevAgeGroupRef.current && prevAgeGroupRef.current !== ageGroup) {
        const currentFormations = formationsRef.current;
        const hasInvalidFormations = 
          (currentFormations.primaryInPossession && !currentFormations.primaryInPossession.isCustom && !formationsForAge.find(f => f.id === currentFormations.primaryInPossession?.formationId)) ||
          (currentFormations.primaryOutOfPossession && !currentFormations.primaryOutOfPossession.isCustom && !formationsForAge.find(f => f.id === currentFormations.primaryOutOfPossession?.formationId)) ||
          (currentFormations.secondaryInPossession && !currentFormations.secondaryInPossession.isCustom && !formationsForAge.find(f => f.id === currentFormations.secondaryInPossession?.formationId)) ||
          (currentFormations.secondaryOutOfPossession && !currentFormations.secondaryOutOfPossession.isCustom && !formationsForAge.find(f => f.id === currentFormations.secondaryOutOfPossession?.formationId));
        
        if (hasInvalidFormations) {
          // Warn user and clear formations
          const confirmed = window.confirm(
            `The age group has changed from ${prevAgeGroupRef.current} to ${ageGroup}. The selected formations are not valid for this age group and will be cleared. Continue?`
          );
          
          if (confirmed) {
            onChange({
              primaryInPossession: undefined,
              primaryOutOfPossession: undefined,
              secondaryInPossession: undefined,
              secondaryOutOfPossession: undefined
            });
          }
        }
      }
      
      // Update the ref for next comparison
      prevAgeGroupRef.current = ageGroup;
    }
  }, [ageGroup, onChange]);

  const handleModeChange = (newMode: 'simple' | 'advanced') => {
    if (mode !== newMode && (formations.primaryInPossession || formations.primaryOutOfPossession)) {
      const confirmed = window.confirm(
        'Switching modes will clear your current formation selections. Are you sure you want to continue?'
      );
      if (!confirmed) return;
      
      // Clear all formations
      onChange({
        primaryInPossession: undefined,
        primaryOutOfPossession: undefined,
        secondaryInPossession: undefined,
        secondaryOutOfPossession: undefined
      });
    }
    setMode(newMode);
  };

  const handleFormationChange = (type: FormationType, formationId: string) => {
    if (formationId === 'custom') {
      // Show custom input
      return;
    }

    if (!formationId) {
      // Clear formation
      onChange({
        ...formations,
        [type]: undefined
      });
      return;
    }

    const formation = availableFormations.find(f => f.id === formationId);
    if (formation) {
      onChange({
        ...formations,
        [type]: {
          formationId: formation.id,
          notation: formation.notation,
          name: formation.name,
          isCustom: false
        }
      });
    }
  };

  const handleCustomFormationSave = (type: FormationType) => {
    const custom = customFormation[type];
    if (custom && custom.notation && custom.name) {
      onChange({
        ...formations,
        [type]: {
          formationId: `custom-${Date.now()}`,
          notation: custom.notation,
          name: custom.name,
          isCustom: true
        }
      });
      setCustomFormation({ ...customFormation, [type]: undefined });
    }
  };

  const renderFormationSelect = (
    type: FormationType,
    label: string,
    guidanceKey: 'inPossession' | 'outOfPossession' | 'secondary'
  ) => {
    const isCustomMode = customFormation[type] !== undefined;
    const currentFormation = formations[type];

    return (
      <FormationSection>
        <SectionHeader>
          <SectionTitle>{label}</SectionTitle>
          <InfoButton onClick={() => setShowInfo(type)} type="button" title="View guidance">
            ?
          </InfoButton>
        </SectionHeader>

        {!isCustomMode ? (
          <>
            <div>
              <Label>Select Formation</Label>
              <Select
                value={currentFormation?.formationId || ''}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setCustomFormation({ ...customFormation, [type]: { notation: '', name: '' } });
                  } else {
                    handleFormationChange(type, e.target.value);
                  }
                }}
              >
                <option value="">-- Select a formation --</option>
                {availableFormations.map(formation => (
                  <option key={formation.id} value={formation.id}>
                    {formation.notation} - {formation.name}
                  </option>
                ))}
                <option value="custom">+ Add Custom Formation</option>
              </Select>
            </div>

            {currentFormation && (
              <FormationPreview>
                <strong>{currentFormation.notation}</strong> - {currentFormation.name}
                {currentFormation.isCustom && <RecommendationBadge>Custom</RecommendationBadge>}
              </FormationPreview>
            )}
          </>
        ) : (
          <CustomFormationInput>
            <div>
              <Label>Formation Notation (e.g., 1-4-4-2)</Label>
              <Input
                type="text"
                placeholder="1-4-4-2"
                value={customFormation[type]?.notation || ''}
                onChange={(e) => setCustomFormation({
                  ...customFormation,
                  [type]: { ...customFormation[type]!, notation: e.target.value }
                })}
              />
            </div>
            <div>
              <Label>Formation Name</Label>
              <Input
                type="text"
                placeholder="e.g., Balanced 4-4-2"
                value={customFormation[type]?.name || ''}
                onChange={(e) => setCustomFormation({
                  ...customFormation,
                  [type]: { ...customFormation[type]!, name: e.target.value }
                })}
              />
            </div>
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
              <Button type="button" onClick={() => handleCustomFormationSave(type)}>
                Save Custom Formation
              </Button>
              <Button 
                type="button" 
                $variant="secondary" 
                onClick={() => setCustomFormation({ ...customFormation, [type]: undefined })}
              >
                Cancel
              </Button>
            </div>
          </CustomFormationInput>
        )}

        {showInfo === type && (
          <InfoModal $isOpen={true} onClick={() => setShowInfo(null)}>
            <InfoModalContent onClick={(e) => e.stopPropagation()}>
              <InfoModalTitle>{FORMATION_GUIDANCE[guidanceKey].title}</InfoModalTitle>
              <InfoModalDescription>
                {FORMATION_GUIDANCE[guidanceKey].description}
              </InfoModalDescription>
              <InfoModalTips>
                {FORMATION_GUIDANCE[guidanceKey].tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </InfoModalTips>
              <Button type="button" onClick={() => setShowInfo(null)} style={{ marginTop: 'var(--spacing-lg)' }}>
                Close
              </Button>
            </InfoModalContent>
          </InfoModal>
        )}
      </FormationSection>
    );
  };

  if (!ageGroup) {
    return (
      <WarningMessage>
        Please select an age group first to see recommended formations.
      </WarningMessage>
    );
  }

  return (
    <Container>
      <ModeToggle>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Formation Mode:</span>
        <ModeButton $active={mode === 'simple'} onClick={() => handleModeChange('simple')} type="button">
          Simple
        </ModeButton>
        <ModeButton $active={mode === 'advanced'} onClick={() => handleModeChange('advanced')} type="button">
          Advanced
        </ModeButton>
      </ModeToggle>

      {mode === 'simple' ? (
        <>
          {renderFormationSelect('primaryInPossession', 'Preferred Formation', 'inPossession')}
          <FormationPreview style={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
            💡 Tip: In simple mode, select one formation that your team will use most often. 
            Switch to advanced mode to configure separate formations for attacking and defending.
          </FormationPreview>
        </>
      ) : (
        <>
          {renderFormationSelect('primaryInPossession', 'Primary In-Possession Formation', 'inPossession')}
          {renderFormationSelect('primaryOutOfPossession', 'Primary Out-of-Possession Formation', 'outOfPossession')}
          {renderFormationSelect('secondaryInPossession', 'Secondary In-Possession Formation (Optional)', 'secondary')}
          {renderFormationSelect('secondaryOutOfPossession', 'Secondary Out-of-Possession Formation (Optional)', 'secondary')}
        </>
      )}
    </Container>
  );
};
